import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, Typography } from 'antd';
import { type ReactNode, useCallback, useEffect } from 'react';
import styles from './NavDrawer.module.scss';

const { Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  // Ordered list of record keys for prev/next navigation. Empty disables the footer.
  navKeys?: string[];
  currentKey?: string | null;
  onNavigate?: (key: string) => void;
  width?: number;
  children: ReactNode;
};

// Reusable detail-drawer shell: a right-side Drawer with a sticky padding-less body and an optional
// prev/next footer driven by keyboard arrows. Shared by the admin idea and praise drawers.
export const NavDrawer = ({ open, onClose, navKeys = [], currentKey = null, onNavigate, width, children }: Props) => {
  const currentIdx = currentKey ? navKeys.indexOf(currentKey) : -1;
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx !== -1 && currentIdx < navKeys.length - 1;

  const goPrev = useCallback(() => {
    const prev = navKeys[currentIdx - 1];
    if (hasPrev && onNavigate && prev) onNavigate(prev);
  }, [hasPrev, onNavigate, navKeys, currentIdx]);

  const goNext = useCallback(() => {
    const next = navKeys[currentIdx + 1];
    if (hasNext && onNavigate && next) onNavigate(next);
  }, [hasNext, onNavigate, navKeys, currentIdx]);

  useEffect(() => {
    if (!open || !onNavigate) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
      if (el.isContentEditable) return;
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onNavigate, goPrev, goNext]);

  const footer = onNavigate ? (
    <Flex align="center" justify="space-between">
      <Button icon={<LeftOutlined />} disabled={!hasPrev} onClick={goPrev}>
        Prev
      </Button>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {currentIdx !== -1 ? `${currentIdx + 1} / ${navKeys.length}` : ''}
      </Text>
      <Button iconPosition="end" icon={<RightOutlined />} disabled={!hasNext} onClick={goNext}>
        Next
      </Button>
    </Flex>
  ) : null;

  return (
    <Drawer
      className={styles.drawer}
      open={open}
      onClose={onClose}
      width={width ?? Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 80 : 720)}
      destroyOnClose
      closable
      title={null}
      maskClosable
      footer={footer}
    >
      {children}
    </Drawer>
  );
};
